import {
    IQueryExpr,
    NotificationComponentConfig,
    Query,
    QuerySelectExpr,
    UserDefinedNotificationQueryOptions,
    UserDefinedNotificationThreshold,
} from '@apis/Notification/model';
import { UdEvaluatedAlertCondition } from '@apis/Notification/model/udEvaluatedAlertCondition';
import { QueryExpr, QueryOperation } from '@apis/Resources';
import { ActionIcon, Box, Group, Popover, Skeleton, Text, Tooltip } from '@mantine/core';
import { useRerender } from '@react-hookz/web';
import { SettingsDivider } from '@root/Components/DashboardLayout/Charts/Design';
import { DatasourceSchemaContext } from '@root/Components/Filter/DatasourceContext';
import { Picker } from '@root/Components/Picker/Picker';
import { ExprPickerAdornment } from '@root/Components/Settings/ExprSettings';
import { createFilterSettingsInput } from '@root/Components/Settings/FilterSettings';
import { IInputSpecBuilderType, IMultiItemPickerSpec, InputSpecBuilder } from '@root/Components/Settings/InputSpec';
import { FormElement, SettingsFormElement } from '@root/Components/Settings/SettingsForms';
import { InputSpecInput, SettingsInput } from '@root/Components/Settings/SettingsInputs';
import { RichText, RichTextItem, RichTextRun, RichTextRuns } from '@root/Components/Text/RichText';
import { FillerSwitch } from '@root/Design/Filler';
import { AnchorButton } from '@root/Design/Primitives';
import {
    SettingsInfoRow,
    SettingsInfoRowText,
    SettingsInputRow,
    SettingsLabel,
    SettingsSectionBodyDivider,
    SettingsSectionItem,
} from '@root/Design/Settings';
import { RequestThrottlingService } from '@root/Services/AsyncBundler';
import { EventEmitter, useEvent, useEventValue, useToggle } from '@root/Services/EventEmitter';
import { FormatService } from '@root/Services/FormatService';
import { exprBuilder, groupExprs, ISupportSubQuery, naiveJsonCopy, traverseExpr, traverseExprDepthFirst } from '@root/Services/QueryExpr';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
    Bell,
    BellRinging,
    ChartBar,
    ChartDonut,
    Edit,
    EqualNot,
    Filter,
    Hash,
    Keyboard,
    List,
    MathFunction,
    Percentage,
    Plus,
    Settings,
    Timeline,
    Trash,
} from 'tabler-icons-react';
import { ComponentAttributeService, IComponentAttribute, IComponentAttributeSet } from './ComponentDescriptor';

interface IAlertSettings<T> {
    optionId: string;
    description: string;
    brief: string;
    model: T;
}
interface IThresholdPresentationOptions {
    settings?: IAlertSettings<any>[];
}

interface IAlertSettingsProps {
    schemaCtx: DatasourceSchemaContext;
    componentConfig: NotificationComponentConfig;
    threshold: UserDefinedNotificationThreshold;
    onEvalAlert: (dataSnapshot?: string) => Promise<UdEvaluatedAlertCondition>;
    onChange: (threshold: UserDefinedNotificationThreshold) => void;
    configChanged: EventEmitter<NotificationComponentConfig>;
}
export function AlertSettings(props: IAlertSettingsProps) {
    const { configChanged, componentConfig, onChange, onEvalAlert, schemaCtx, threshold } = props;
    const model = useMemo(() => new AlertSettingsModel(threshold, schemaCtx, onEvalAlert).init(componentConfig), [threshold, schemaCtx, onEvalAlert]);
    useEvent(configChanged, model.updateComponentConfig);
    useEvent(model.viewInvalidated);
    useEffect(() => model.setOnChange(onChange), [onChange, model]);

    return <FillerSwitch noData={!model.options}>{() => <AlertSettingsItems model={model} />}</FillerSwitch>;
}

// #region Alert Type Picker
interface IAlertOptionPickerProps {
    options: AlertSettingsOptionsCollection;
    selected?: IAlertSettingsOption;
    onSelect: (options: AlertSettingsOption) => void;
}
function AlertOptionPicker(props: IAlertOptionPickerProps) {
    const { onSelect, options, selected } = props;
    const [opened, { close, toggle }] = useToggle(false);

    const handleSelect = useCallback(
        (option: AlertSettingsOption[]) => {
            onSelect(option[0]);
            close();
        },
        [onSelect]
    );
    const items = useMemo(() => options.options.slice(), [options]);
    const selection = useMemo(() => (selected ? [selected] : []), [selected]);
    return (
        <Popover position={selected ? 'bottom-start' : 'bottom'} offset={0} shadow="md" opened={opened} onClose={close}>
            <Popover.Dropdown sx={{ borderColor: '#0003' }} p={0}>
                <Picker
                    mode="single"
                    width={400}
                    noFilter
                    onChange={handleSelect}
                    items={items}
                    selections={selection}
                    nameAccessor="name"
                    inlineFullText
                    minimizeHeight
                    height={300}
                />
            </Popover.Dropdown>
            <Popover.Target>
                <Box>
                    {selected ? (
                        <SettingsInputRow>
                            <AnchorButton
                                iconPosition="right"
                                iconOnHover
                                icon={<Edit size={14} />}
                                size="sm"
                                text={selected?.name ?? 'Select alert type...'}
                                onClick={toggle}
                            />
                        </SettingsInputRow>
                    ) : (
                        <SettingsSectionItem anchor onClick={toggle}>
                            <AnchorButton icon={<Plus size={14} />} size="sm" text="Select alert type..." onClick={() => {}} />
                        </SettingsSectionItem>
                    )}
                </Box>
            </Popover.Target>
        </Popover>
    );
}
// #endregion Alert Type Picker

interface IAlertSettingsItemsProps {
    model: AlertSettingsModel;
}
function AlertSettingsItems({ model }: IAlertSettingsItemsProps) {
    return (
        <>
            {model.settingsModels.map((item, i) => (
                <AlertSettingsItem key={i} model={item} />
            ))}
        </>
    );
}

function AlertSettingsItem(props: { model: AlertSettingsItemModel }) {
    const { model } = props;
    const rerender = useRerender();
    const description = model.settings.description;
    const onChanged = useCallback(() => {
        model.inputChanged();
        rerender();
    }, [model.inputChanged, model.formElements]);

    return (
        <>
            {model.options.options.length < 2 ? null : (
                <>
                    <AlertOptionPicker onSelect={model.changeOption} options={model.options} selected={model.option} />
                    <SettingsSectionBodyDivider />
                </>
            )}
            {model.formElements.map((el, i) => (
                <SettingsFormElement key={i} element={el} onChanged={onChanged} />
            ))}
            {description ? (
                <>
                    <SettingsSectionBodyDivider />
                    <SettingsInfoRow>
                        <SettingsLabel>Alert Description:</SettingsLabel>
                        <SettingsInfoRowText indent>{description}</SettingsInfoRowText>
                    </SettingsInfoRow>
                    <AlertEvaluationDescription model={model} />
                </>
            ) : null}
        </>
    );
}

function AlertEvaluationDescription(props: { model: AlertSettingsItemModel }) {
    const evalResult = useEventValue(props.model.evalResult);
    const description = !evalResult ? ('loading' as const) : props.model.getEvalDescription();
    return (
        <>
            {!description ? null : (
                <SettingsInfoRow>
                    <SettingsLabel>Alert Evaluation:</SettingsLabel>
                    <SettingsInfoRowText indent>
                        {description === 'loading' ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <Skeleton my={2} key={i} height={26} visible>
                                    <SettingsInfoRowText>Evaluating</SettingsInfoRowText>
                                </Skeleton>
                            ))
                        ) : (
                            <RichText text={description} />
                        )}
                    </SettingsInfoRowText>
                </SettingsInfoRow>
            )}
        </>
    );
}

interface IAlertSettingsOption<T = {}> {
    id: string;
    name: string;
    defaultModel: T;
    form: (model: T) => FormElement[];
    onChange?: () => void;
    toQuery: (model: T) => { query?: Query; condition?: IQueryExpr };
    describe?: (model: T) => string;
    describeEval?: (model: T, evalResult: UdEvaluatedAlertCondition) => RichTextItem;
    updateModel?: (currentModel: T, attrChanges: Set<AttributeChangeTypes>) => boolean;
}
type AlertSettingsOption = IAlertSettingsOption<any>;

class AlertSettingsModel {
    private onChange: (threshold: UserDefinedNotificationThreshold) => void = () => {};
    private _settingsModels?: AlertSettingsItemModel[];

    public readonly viewInvalidated = EventEmitter.empty();
    public options = new AlertSettingsOptionsCollection([], undefined);
    public get settingsModels() {
        return (this._settingsModels ??= this.createItemModels());
    }
    public get pres() {
        return (this.threshold.PresentationOptions ??= { settings: [] }) as Required<IThresholdPresentationOptions>;
    }
    public readonly evaluationResult = new EventEmitter<UdEvaluatedAlertCondition | undefined>(undefined);
    public readonly evalRequestor: { clearSnapshot: () => void; request: () => Promise<void> };

    public constructor(
        public readonly threshold: UserDefinedNotificationThreshold,
        public readonly schemaCtx: DatasourceSchemaContext,
        evalAlert: (dataSnapshot?: string) => Promise<UdEvaluatedAlertCondition>
    ) {
        this.evalRequestor = this.createRequestEval(evalAlert);
    }

    public setOnChange(onChange: (threshold: UserDefinedNotificationThreshold) => void) {
        this.onChange = (threshold: UserDefinedNotificationThreshold) => {
            onChange(threshold);
            this.evalRequestor.request();
        };
    }

    public raiseSettingsChange() {
        this.onChange(this.threshold);
    }

    public init(componentConfig: NotificationComponentConfig) {
        const options = (this.options = this.createOptions(componentConfig));
        this.updateOptions(options, this.pres.settings.length === 0);

        return this;
    }

    private createRequestEval(evalAlert: (dataSnapshot?: string) => Promise<UdEvaluatedAlertCondition>) {
        let dataSnapshot: string | undefined = undefined;
        const requestor = async () => {
            let result: UdEvaluatedAlertCondition = {
                ConditionMet: null,
                EvaluationFailed: true,
                EvaluationIssue: 'Failed to evaluate alert conditions',
            };
            try {
                result = await evalAlert(dataSnapshot);
                if (result.DataSnapshot) {
                    dataSnapshot = result.DataSnapshot;
                }
            } catch (e) {
                console.error(e);
            }
            return result;
        };
        return {
            clearSnapshot: () => {
                dataSnapshot = undefined;
            },
            request: async () => {
                this.evaluationResult.emit(undefined);
                const result = await requestor();
                this.evaluationResult.emit(result);
            },
        };
    }

    // #region Options
    public updateComponentConfig = (config: NotificationComponentConfig) => {
        const options = this.createOptions(config);
        this.updateOptions(options, false);
        this.viewInvalidated.emit();
    };

    public selectOption = (option: AlertSettingsOption) => {
        this.clearSettings();
        this.pres.settings.push(this.createSetting(option));
        this.viewInvalidated.emit();
    };

    private updateOptions(nextOptions: AlertSettingsOptionsCollection, initialize: boolean) {
        const { compatible, differences } = nextOptions.optionsCompatible(this.options);
        const { hasAnyIncompatibility, allCosmetic, items } = differences;
        const currEmpty = this.options.isEmpty || initialize;
        if (items.has('dataset-id') || items.has('filter-id')) {
            this.evalRequestor.clearSnapshot();
        }
        this.options = nextOptions;
        if (!compatible || hasAnyIncompatibility || currEmpty) {
            if (this.tryUpdateSettings(items)) {
                this.settingsModels.forEach((s) => s.resetOption());
            } else {
                this.clearSettings();
                this.pres.settings.push(this.createSetting(nextOptions.options[0]));
            }
        } else if (allCosmetic) {
            this.settingsModels.forEach((s) => s.resetOption());
        }
    }

    private createOptions(componentConfig: NotificationComponentConfig) {
        const attrSvc = new ComponentAttributeService(this.schemaCtx);
        const attrs = attrSvc.createAttributes(componentConfig);
        return AlertOptionsBuilder.createOptions(this.schemaCtx, attrs);
    }
    // #endregion Options

    // #region Settings
    private tryUpdateSettings(changeTypes: Set<AttributeChangeTypes>) {
        let result = this.pres.settings.length > 0;
        for (const setting of this.pres.settings) {
            const option = this.options.getById(setting.optionId);
            if (!option || !option.updateModel || !option.updateModel(setting.model, changeTypes)) {
                result = false;
                break;
            }
        }
        return result;
    }

    public refreshThresholdQueries = (settingsModel: AlertSettingsItemModel) => {
        // note: multiple queries/conditions not yet supported
        const { query, condition } = settingsModel.option.toQuery(settingsModel.settings.model);
        this.threshold.ThresholdQuery = query;
        this.threshold.ThresholdCondition = condition;

        this.raiseSettingsChange();
        this.evalRequestor.request();
    };

    private clearSettings() {
        this.pres.settings.splice(0, Infinity);
        this._settingsModels = undefined;
    }

    private createItemModels() {
        const settings = this.pres.settings;
        return settings
            .map((s) => {
                const option = this.options.getById(s.optionId);
                return !option ? null! : new AlertSettingsItemModel(option, s, this);
            })
            .filter((t) => !!t);
    }

    private createSetting(option: IAlertSettingsOption) {
        const model = naiveJsonCopy(option.defaultModel);
        const description = option.describe?.(model) ?? '';
        return {
            optionId: option.id,
            description,
            model,
            brief: option.name,
        };
    }
    // #endregion Settings
}

class AlertSettingsItemModel {
    public readonly formElements: FormElement[] = [];
    private _option!: AlertSettingsOption;

    public get option() {
        return this._option;
    }
    public get options() {
        return this.parent.options;
    }

    public get evalResult() {
        return this.parent.evaluationResult;
    }

    public constructor(option: AlertSettingsOption, public readonly settings: IAlertSettings<any>, private readonly parent: AlertSettingsModel) {
        this.setOption(option);
    }

    public resetOption() {
        this.setOption(this.options.getById(this.settings.optionId)!);
    }
    private setOption(option: AlertSettingsOption) {
        this._option = option;
        this.applyOption();
        this.option.onChange?.();
        this.parent.refreshThresholdQueries(this);
    }

    private applyOption() {
        this.formElements.splice(0, Infinity, ...this.option.form(this.settings.model));
        this.settings.description = this.option.describe?.(this.settings.model) ?? '';
    }

    public changeOption = (option: AlertSettingsOption) => {
        this.setOption(option);
    };

    public inputChanged = () => {
        this.settings.description = this.option.describe?.(this.settings.model) ?? '';
        this.option.onChange?.();
        this.parent.refreshThresholdQueries(this);
    };

    public getEvalDescription() {
        const evalResult = this.evalResult.value;
        if (evalResult) {
            return this.option.describeEval?.(this.settings.model, evalResult) ?? null;
        }
        return null;
    }
}

type AttributeChangeTypes = 'type' | 'dim' | 'metric' | 'format' | 'alias' | 'dataset-id' | 'filter-id';
class AlertSettingsOptionsCollection {
    private readonly optionLookup: Map<string, AlertSettingsOption>;

    public get isEmpty() {
        return this.options.length === 0;
    }

    public constructor(public readonly options: ReadonlyArray<AlertSettingsOption>, private readonly attrs?: IComponentAttributeSet) {
        this.optionLookup = new Map(options.map((t) => [t.id, t]));
    }

    public getById(id: string) {
        return this.optionLookup.get(id);
    }

    public optionsCompatible(other: AlertSettingsOptionsCollection) {
        const compatible = other.options.length === this.options.length && other.options.every((o) => this.getById(o.id));
        return { compatible, differences: this.componentIncompatibility(other.attrs) };
    }

    private componentIncompatibility(otherAttrs?: IComponentAttributeSet) {
        const cosmeticDiffs = new Set<AttributeChangeTypes>(['format', 'alias', 'filter-id']);
        const result = new Set<AttributeChangeTypes>();

        if (this.attrs && otherAttrs) {
            const evaluators: Array<{ accessor: (attr: IComponentAttributeSet) => any; type: AttributeChangeTypes }> = [
                { type: 'dim', accessor: (v) => v.dimensions[0]?.selectExpr?.Expr },
                { type: 'metric', accessor: (v) => v.metrics[0]?.selectExpr?.Expr },
                { type: 'format', accessor: (v) => v.metrics[0]?.format },
                {
                    type: 'alias',
                    accessor: (v) => [
                        v.metrics.map((m) => m.name),
                        v.dimensions.map((m) => m.name),
                        Object.values(v.descriptor.dimensionComponent ?? {}),
                    ],
                },
                { type: 'type', accessor: (v) => v.descriptor.subType },
                { type: 'dataset-id', accessor: (v) => v.datasetId },
                { type: 'filter-id', accessor: (v) => v.filterId },
            ];

            for (const { accessor, type } of evaluators) {
                const match = JSON.stringify(accessor(this.attrs)) === JSON.stringify(accessor(otherAttrs));
                if (!match) {
                    result.add(type);
                }
            }
        } else {
            result.add('type');
        }

        const allCosmetic = [...result.values()].every((v) => cosmeticDiffs.has(v));
        const hasAnyIncompatibility = result.size > 0 && !allCosmetic;

        return {
            allCosmetic: [...result.values()].every((v) => cosmeticDiffs.has(v)),
            hasAnyIncompatibility,
            hasAnyDifference: result.size > 0,
            items: result,
        };
    }
}

type OptionComponentSubset = {
    input: QueryExpr;
    agg: 'any' | 'sum' | 'min' | 'max' | 'avg' | 'percent';
    subset: 'some' | 'all';
    rank: 'asc' | 'desc' | 'filter' | 'none';
    rankCutoff: number;
    comparitor: 'gt' | 'lt' | 'eq' | 'ne' | 'gte' | 'lte';
    subsetComparitor: 'agg-any' | 'agg-others' | 'pct-any' | 'pct-others' | 'pct-all';
    dimFilter: string[];
    filter: QueryExpr[];
};
class AlertOptionsBuilder {
    private readonly fmtSvc: FormatService;
    private static readonly defaultSubset: OptionComponentSubset = {
        agg: 'sum',
        input: { Value: null },
        subset: 'some',
        comparitor: 'gt',
        rank: 'none',
        subsetComparitor: 'pct-others',
        rankCutoff: 3,
        filter: [],
        dimFilter: [],
    };

    private constructor(private readonly baseSchemaCtx: DatasourceSchemaContext, private readonly attrs: IComponentAttributeSet) {
        this.fmtSvc = FormatService.instance;
    }

    public static createOptions(baseSchemaCtx: DatasourceSchemaContext, attrs: IComponentAttributeSet) {
        const builder = new AlertOptionsBuilder(baseSchemaCtx, attrs);
        return builder.createOptions();
    }

    private createOptions(): AlertSettingsOptionsCollection {
        const result: IAlertSettingsOption[] = [];

        if (this.attrs.hasMetrics) {
            result.push(...this.createComponentOptions());
        }

        return new AlertSettingsOptionsCollection(result, this.attrs);
    }

    //#region Component-specific options
    private createComponentOptions(): IAlertSettingsOption[] {
        const componentType = this.attrs.descriptor.subType;
        switch (componentType ?? '') {
            case 'grid':
                return [...this.createGridSubsetInput()];
            case 'kpi':
                return [...this.createKpiAlertOptionsInput()];
            default:
                const attr = this.attrs.metrics[0];
                return this.createComponentThresholdOptions(attr);
        }
    }
    private createComponentThresholdOptions(attr: IComponentAttribute): IAlertSettingsOption[] {
        const xb = this.createXb();
        const attrs = this.attrs.subset((t) => t !== attr);
        const metricInputMode = attrs.hasMetrics ? undefined : ('constant' as const);

        const funcOptions: Array<{ value: OptionComponentSubset['agg']; label: string }> = [
            { value: 'any', label: `Any "${attr.name}"` },
            { value: 'sum', label: `Total "${attr.name}"` },
            { value: 'avg', label: `Average "${attr.name}"` },
            { value: 'max', label: `Highest "${attr.name}"` },
            { value: 'min', label: `Lowest "${attr.name}"` },
        ];
        const model = { ...AlertOptionsBuilder.defaultSubset, input: { Value: null } as unknown as { Value: number } };
        const { comparitorOptions, optionLookup: compLookup } = this.getComparitorOptions();

        const result: AlertSettingsOption[] = [];
        result.push(
            this.createOption('com-sub-thresh', `When ${attr.name} reaches a threshold`, '', model)
                .form((b, model, evt) => [
                    b
                        .type('string')
                        .options({ label: 'Function', defaultValue: 'sum', icon: <MathFunction />, options: funcOptions })
                        .presentation({ align: 'center', width: 170 })
                        .map((o) => o.agg)
                        .bind(model),
                    ...this.createComponentSubsetInput(evt, attr, model, () => model.agg !== 'any'),
                    b
                        .type('string')
                        .options({ label: 'Comparison Type', defaultValue: 'gt', icon: <EqualNot />, options: comparitorOptions })
                        .presentation({ align: 'center', width: 180 })
                        .map((o) => o.comparitor)
                        .bind(model),
                    this.createDynamic(evt, () => {
                        const percent = model.agg !== 'any' && model.rank && model.rank !== 'none' && model.subsetComparitor.startsWith('pct');
                        const fractions = percent ? 1 : undefined;

                        return [
                            this.createNumberInput(b, 'Alert threshold', { attrs, mode: metricInputMode, icon: <Bell />, percent, fractions })
                                .map((o) => o.input)
                                .bind(model),
                        ];
                    }),
                    this.createValidationWarning(evt, () => this.validateComponentSubsetModel(model, attr), 'Configuration is incomplete.'),
                ])
                .toQuery((model) => {
                    const { agg, input, comparitor } = model;
                    const issues = this.validateComponentSubsetModel(model, attr);

                    if (issues.length) {
                        return {};
                    } else if (agg === 'any') {
                        return { condition: xb.resolve(xb.op<boolean>(comparitor, xb.model[attr.alias], xb.fromExpr(input))) };
                    }

                    const { rank } = model;
                    if (!rank || rank === 'none') {
                        return {
                            query: { Select: xb.select({ total: xb.op<number>(agg, xb.model[attr.alias]) }) },
                            condition: xb.resolve(xb.op<boolean>(comparitor, xb.model['total'], xb.fromExpr(input))),
                        };
                    }

                    const { dimFilter, rankCutoff, subsetComparitor } = model;
                    const { dimensions, hasDimensions, postSort } = this.attrs;
                    const dim = hasDimensions ? dimensions[0] : null;
                    const subsetType = subsetComparitor.endsWith('others') ? 'others' : subsetComparitor.endsWith('all') ? 'all' : ('any' as const);
                    const subsetMode = subsetComparitor.startsWith('pct') ? ('percent' as const) : agg;

                    const parameters: Record<string, IQueryExpr> = {};
                    const query: Query = {
                        Select: [],
                        Parameters: parameters,
                    };
                    const pResult = xb.model['__pResults'];
                    const subXb = xb as ISupportSubQuery & typeof xb;
                    const metricAgg = xb.op<number>(agg, xb.model[attr.alias]);
                    const dimAlias = dim?.alias!;

                    parameters.pRollup = subXb.subQuery(pResult, {
                        Select: xb.select({
                            [dimAlias]: xb.model[dimAlias],
                            [attr.alias]: xb.sum(xb.model[attr.alias]),
                        }),
                    });

                    const isFilter = model.rank === 'filter';
                    parameters.pSource = xb.resolve(xb.model.pRollup);
                    type SkipTake = { Take?: number; Skip?: number };
                    let selectedSkipTake: SkipTake = {};
                    let othersSkipTake: SkipTake = {};

                    if (!isFilter) {
                        const sortField = !postSort ? null : postSort.sortBy === 'label' ? dimAlias : attr.alias;
                        const visSortDir = !postSort ? null : postSort.sortDir === 'asc' ? 'Asc' : ('Desc' as const);
                        const sortDir = !visSortDir ? null : rank === 'asc' ? visSortDir : visSortDir === 'Desc' ? 'Asc' : ('Desc' as const);

                        parameters.pSource = subXb.subQuery(xb.model.pRollup, {
                            Sort: !sortField || !sortDir ? null : [{ Direction: sortDir, Expr: { Field: sortField } }],
                        });

                        selectedSkipTake = { Take: rankCutoff };
                        if (subsetType !== 'all') {
                            othersSkipTake = { Skip: rankCutoff };
                        }
                    }

                    const filter = isFilter ? xb.fromExpr<string>({ Field: dimAlias }).eq(dimFilter) : null;

                    parameters.pSelectedSet = subXb.subQuery(xb.model.pSource, { ...selectedSkipTake });
                    parameters.pSelected = subXb.scalarSubQuery(xb.model.pSelectedSet, 'total', {
                        Select: xb.select({
                            total: filter ? xb.aggIf(filter, metricAgg) : metricAgg,
                        }),
                    });

                    parameters.pOthersSet = subXb.subQuery(xb.model.pSource, { ...othersSkipTake });
                    const othersQuery: Query = {
                        Select: xb.select({
                            selected: xb.model.pSelected,
                            total: filter ? xb.aggIf(xb.not(filter), metricAgg) : metricAgg,
                        }),
                    };

                    if (subsetType === 'any') {
                        othersQuery.Select!.push(
                            ...xb.select({
                                dim: xb.model[dimAlias],
                            })
                        );
                        othersQuery.Having = !filter ? undefined : xb.resolve(xb.not(filter));

                        parameters.pOthers = subXb.subQuery(xb.model.pOthersSet, othersQuery);
                        parameters.pLeftOp = subXb.scalarArrSubQuery(xb.model.pOthers, 'total', {
                            Select: xb.select({
                                total:
                                    subsetMode === 'percent'
                                        ? xb.model.pSelected.dividedBy(xb.model.pSelected.plus(xb.model.total))
                                        : xb.model.pSelected.minus(xb.model.total),
                            }),
                        });
                    } else {
                        parameters.pOthers = subXb.scalarSubQuery(xb.model.pOthersSet, 'total', othersQuery);
                        const totalExpr =
                            subsetType === 'all'
                                ? xb.model.pSelected.dividedBy(xb.model.pOthers)
                                : subsetMode === 'percent'
                                ? xb.model.pSelected.dividedBy(xb.model.pSelected.plus(xb.model.pOthers))
                                : xb.model.pSelected.minus(xb.model.pOthers);
                        parameters.pLeftOp = subXb.scalarSubQuery(subXb.records([{}]), 'total', {
                            Select: xb.select({
                                total: totalExpr,
                            }),
                        });
                    }

                    query.Select = xb.select({
                        selected: xb.model.pSelected,
                        others: xb.model.pOthers,
                        total: xb.model.pLeftOp,
                    });

                    return {
                        query,
                        condition: xb.resolve(xb.op<boolean>(model.comparitor, xb.model.total, xb.fromExpr(input))),
                    };
                })
                .describe((model) => {
                    const { agg, input, comparitor, rank, subsetComparitor, dimFilter, rankCutoff } = model;
                    const { dimensions, descriptor } = this.attrs;
                    const { plural: compPlrl, name: compSing, asc: compAsc, desc: compDesc } = descriptor?.dimensionComponent ?? {};
                    const { format: metricFmt, name: metricName } = attr;
                    const inputLbl = input.Value === null ? 'Not Set' : this.fmtSvc.getFormatter(metricFmt ?? 'number').format(input.Value);
                    const comparitorName = (compLookup.get(comparitor) ?? comparitor).toLocaleLowerCase();
                    const aggLbl = agg === 'avg' ? 'average' : agg === 'sum' ? `sum of` : agg === 'min' ? 'lowest' : 'highest';

                    if (agg === 'any') {
                        return `Alert if the "${metricName}" for any ${compSing} is ${comparitorName} ${inputLbl}`;
                    }
                    if (!rank || rank === 'none') {
                        const setPrepLbl = agg === 'avg' ? 'over all' : agg === 'sum' ? 'for all' : 'out of all';
                        return `Alert if the ${aggLbl} "${metricName}" ${setPrepLbl} ${compPlrl} is ${comparitorName} ${inputLbl}`;
                    }

                    const subsetType = subsetComparitor.endsWith('others') ? 'others' : subsetComparitor.endsWith('all') ? 'all' : ('any' as const);
                    const subsetMode = subsetComparitor.startsWith('pct') ? ('percent' as const) : agg;
                    const pctLbl = this.fmtSvc.formatDecimal(input.Value * 100, 1) + '%';

                    const rankDir = rank === 'asc' ? compAsc : rank === 'desc' ? compDesc : null;
                    const randCt = rankCutoff === 1 ? '' : `${rankCutoff} `;
                    const subsetPlrl = rank === 'filter' ? dimFilter.length > 1 : rankCutoff > 1;
                    const compName = subsetPlrl ? compPlrl : compSing;
                    const dimName = dimensions.length > 1 ? dimensions[0].name : '';
                    const dimFilterLbls = dimFilter.map((t) => `"${t}"`);
                    let subsetLbl =
                        rank !== 'filter'
                            ? `first ${randCt}${compName} (${rankDir})`
                            : dimFilter.length
                            ? `${compName} for ${this.fmtSvc.userFriendlyJoin(dimFilterLbls, 'and')}`
                            : `selected ${dimName} options`;

                    if (subsetMode === 'percent') {
                        const pctRightOpLbl =
                            subsetType === 'any'
                                ? `when compared with any single other ${compSing}`
                                : subsetType === 'all'
                                ? `when compared to the overall ${aggLbl} "${metricName}"`
                                : `when compared to all other ${compPlrl}`;
                        const comparitorPrf =
                            comparitor === 'gte'
                                ? `${pctLbl} or more`
                                : comparitor === 'gt'
                                ? `more than ${pctLbl}`
                                : comparitor === 'lte'
                                ? `${pctLbl} or less`
                                : comparitor === 'lt'
                                ? `less than ${pctLbl}`
                                : comparitor === 'eq'
                                ? pctLbl
                                : comparitor === 'ne'
                                ? `a percent other than ${pctLbl}`
                                : pctLbl;
                        const makesLbl = rankCutoff === 1 ? 'makes' : 'make';
                        return `Alert if the ${subsetLbl} ${makesLbl} up ${comparitorPrf} of the ${aggLbl} "${metricName}" ${pctRightOpLbl}`;
                    } else {
                        const otherLbl =
                            subsetType === 'any'
                                ? `any other single ${compSing}`
                                : subsetType === 'others'
                                ? `all other ${compPlrl}`
                                : `all ${compPlrl}`;
                        return `Alert if the ${subsetLbl} ${aggLbl} "${metricName}" minus that of ${otherLbl} is ${comparitorName} ${inputLbl}`;
                    }
                })
                .describeEval((model, res): RichTextItem => {
                    const issues = this.validateComponentSubsetModel(model, attr);
                    if (issues.length) {
                        return '';
                    }
                    const error = this.getEvaluationError(res);
                    if (error) {
                        return error;
                    }

                    const data = res.EvaluatedData?.[0];

                    const { agg, input, comparitor, rank, subsetComparitor } = model;
                    const pctLbl = this.fmtSvc.formatDecimal(input.Value * 100, 1) + '%';
                    const { descriptor } = this.attrs;
                    const { plural: compPlrl, name: compSing } = descriptor?.dimensionComponent ?? {};
                    const { format: metricFmt, name: metricName } = attr;
                    const valFormatter = this.fmtSvc.getFormatter(metricFmt ?? 'number');
                    let inputLbl = valFormatter.format(input.Value);
                    const comparitorName = (compLookup.get(comparitor) ?? comparitor).toLocaleLowerCase();
                    const aggLbl = agg === 'avg' ? 'average' : agg === 'sum' ? `sum of` : agg === 'min' ? 'lowest' : 'highest';

                    const lines: RichTextRuns[] = [];
                    const triggeredRun: RichTextItem = {
                        text: res.ConditionMet ? ' Yes ' : ' No ',
                        type: 'variable',
                        varKey: 'result',
                        info: res.ConditionMet
                            ? 'Given the currently visualized data, the alert would be sent'
                            : 'Given the currently visualized data, the alert would NOT be sent',
                    };

                    if (agg === 'any') {
                        const matchText = res.ConditionMet ? `At least one ${compSing} is ` : `No single ${compSing} is `;
                        lines.push(`${matchText} ${comparitorName} ${inputLbl}`);
                    } else if (!rank || rank === 'none') {
                        const rawValue = data?.['total'];
                        const valueLbl = typeof rawValue !== 'number' ? 'None' : valFormatter.format(rawValue);
                        lines.push(
                            [{ text: `${this.fmtSvc.titleCase(aggLbl)} "${metricName}" : `, varKey: 'left', type: 'variable' }, valueLbl],
                            [
                                { text: valueLbl, varKey: 'left', type: 'variable' },
                                ` is ${comparitorName}`,
                                { text: inputLbl, varKey: 'right', type: 'variable' },
                            ]
                        );
                    } else {
                        const subsetMode = subsetComparitor.startsWith('pct') ? ('percent' as const) : agg;
                        const subsetType = subsetComparitor.endsWith('others')
                            ? 'others'
                            : subsetComparitor.endsWith('all')
                            ? 'all'
                            : ('any' as const);
                        const aggMetricLbl = `${this.fmtSvc.titleCase(aggLbl)} "${metricName}"`;
                        inputLbl = subsetMode === 'percent' ? pctLbl : inputLbl;

                        const rawSelected = data?.['selected'];
                        const selectedLbl = typeof rawSelected !== 'number' ? 'None' : valFormatter.format(rawSelected);
                        const selectedRun: RichTextRun = { text: selectedLbl, varKey: 'selected', type: 'variable' };

                        lines.push([{ text: `Selected ${compPlrl} `, varKey: 'selected', type: 'variable' }, ` ${aggMetricLbl} : `, selectedLbl]);

                        const rawTotal = data?.['total'];
                        const totalLbl =
                            typeof rawTotal !== 'number'
                                ? 'None'
                                : subsetMode === 'percent'
                                ? this.fmtSvc.formatDecimal(rawTotal * 100, 1) + '%'
                                : valFormatter.format(rawTotal);
                        const totalRun: RichTextRun = { text: totalLbl, varKey: 'total', type: 'variable' };

                        const inputRun: RichTextRun = { text: inputLbl, varKey: 'input', type: 'variable' };
                        if (subsetType === 'any') {
                            const portionsText = subsetMode === 'percent' ? 'Portions' : 'Differences';
                            const portionsLbl: RichTextRun = { text: portionsText, type: 'variable', varKey: 'portion' };
                            const calcLbl: RichTextRuns = [portionsLbl, ` calculated as `];
                            if (subsetMode === 'percent') {
                                lines.push([...calcLbl, selectedRun, ` / (`, `each other ${compSing} + `, selectedRun, `)`]);
                            } else {
                                lines.push([...calcLbl, selectedRun, ` \u2013 `, ` each other ${compSing}`]);
                            }
                            const portionText = subsetMode === 'percent' ? 'portion' : 'difference';
                            const portionLbl: RichTextRun = { text: portionText, type: 'variable', varKey: 'portion' };
                            lines.push([`Any `, portionLbl, ` ${comparitorName}`, inputRun, ': ', triggeredRun]);
                        } else {
                            const rawOthers = data?.['others'];
                            const othersLbl = typeof rawOthers !== 'number' ? 'None' : valFormatter.format(rawOthers);
                            const othersRun: RichTextRun = { text: othersLbl, varKey: 'other', type: 'variable' };
                            const othersText = subsetType === 'all' ? 'All' : 'Other';
                            lines.push([{ text: `${othersText} ${compPlrl} `, varKey: 'other', type: 'variable' }, ` ${aggMetricLbl} : `, othersLbl]);

                            const totalLblRun: RichTextRun = {
                                text: subsetMode === 'percent' ? 'Portion' : 'Difference',
                                type: 'variable',
                                varKey: 'total',
                            };
                            if (subsetMode === 'percent') {
                                if (subsetType === 'all') {
                                    lines.push([totalLblRun, selectedRun, ` / `, othersRun, ` : `, totalLbl]);
                                } else {
                                    lines.push([totalLblRun, selectedRun, ` / (`, othersRun, ' + ', selectedRun, `) : `, totalLbl]);
                                }
                            } else {
                                lines.push([totalLblRun, selectedRun, ` \u2013 `, othersRun, ' : ', totalLbl]);
                            }

                            lines.push(['Finally, ', totalRun, ` is ${comparitorName} `, inputRun, ` : `, triggeredRun]);
                        }
                    }

                    lines.push(['Alert condition met: ', triggeredRun]);
                    return { lines };
                })
                .done()
        );

        return result;
    }

    private validateComponentSubsetModel(model: OptionComponentSubset, metricAttr: IComponentAttribute): string[] {
        const { agg, dimFilter, rankCutoff, rank, input } = model;
        const result: string[] = [];
        const { dimensionComponent } = this.attrs.descriptor;
        const dim = this.attrs.hasDimensions ? this.attrs.dimensions[0] : null;

        if (agg !== 'any') {
            if (rank === 'filter' && !dimFilter.length) {
                result.push(`Select at least one ${dim?.name}.`);
            } else if (rank === 'asc' || rank === 'desc') {
                if (rankCutoff < 1) {
                    result.push(`Select at least one ${dimensionComponent?.name}.`);
                }
            }
        }

        if ('Value' in input && input.Value === null) {
            result.push(`Enter an Alert threshold.`);
        }

        return result;
    }

    private createComponentSubsetInput(
        evt: EventEmitter<void>,
        metricAttr: IComponentAttribute,
        model: OptionComponentSubset,
        condition: () => boolean
    ): FormElement[] {
        const { descriptor } = this.attrs;
        const { subType } = descriptor;

        const result: FormElement[] = [];

        const subsetInputs: FormElement[] = [];
        let rankInput: FormElement | undefined = undefined;
        switch (subType) {
            case 'bar':
                rankInput = this.createDimRankPicker(model, <ChartBar />);
                subsetInputs.push(...this.createBarSubsetInput(evt, model));
                break;
            case 'line':
                rankInput = this.createDimRankPicker(model, <Timeline />, false, 'desc');
                subsetInputs.push(...this.createLineSubsetInput(evt, model));
                break;
            case 'pie':
                rankInput = this.createDimRankPicker(model, <ChartDonut />);
                subsetInputs.push(...this.createPieSubsetInput(evt, model));
                break;
            default:
                return [];
        }

        if (rankInput) {
            subsetInputs.unshift(rankInput);
        }

        result.push(this.createCondition(evt, condition, ...subsetInputs, ...this.createSubsetComparitorInput(evt, metricAttr, model)));

        return result;
    }

    private createBarSubsetInput(evt: EventEmitter<void>, model: OptionComponentSubset): FormElement[] {
        const b = this.inputBuilder<typeof model>();
        return [
            this.createCondition(
                evt,
                () => model.rank === 'asc' || model.rank === 'desc',
                b
                    .type('number')
                    .options({ label: 'Bars', icon: <Hash />, defaultValue: 3, min: 1 })
                    .presentation({ align: 'center' })
                    .map((o) => o.rankCutoff)
                    .bind(model)
            ),
            this.createCondition(
                evt,
                () => model.rank === 'filter',
                this.createDimPickerInput(model, 'dimFilter', 'Bars', { icon: <Filter />, defaultValue: [] })
            ),
        ];
    }

    private createLineSubsetInput(evt: EventEmitter<void>, model: OptionComponentSubset): FormElement[] {
        const b = this.inputBuilder<typeof model>();
        return [
            this.createCondition(
                evt,
                () => model.rank === 'asc' || model.rank === 'desc',
                b
                    .type('number')
                    .options({ label: 'Points', icon: <Hash />, defaultValue: 3, min: 1 })
                    .presentation({ align: 'center' })
                    .map((o) => o.rankCutoff)
                    .bind(model)
            ),
        ];
    }

    private createPieSubsetInput(evt: EventEmitter<void>, model: OptionComponentSubset): FormElement[] {
        const b = this.inputBuilder<typeof model>();
        return [
            this.createCondition(
                evt,
                () => model.rank === 'asc' || model.rank === 'desc',
                b
                    .type('number')
                    .options({ label: 'Slices', icon: <Hash />, defaultValue: 3, min: 1 })
                    .presentation({ align: 'center' })
                    .map((o) => o.rankCutoff)
                    .bind(model)
            ),
            this.createCondition(
                evt,
                () => model.rank === 'filter',
                this.createDimPickerInput(model, 'dimFilter', 'Slices', { icon: <Filter />, defaultValue: [] })
            ),
        ];
    }

    private createSubsetComparitorInput(evt: EventEmitter<void>, metricAttr: IComponentAttribute, model: OptionComponentSubset): FormElement[] {
        const b = this.inputBuilder<typeof model>();
        const { descriptor } = this.attrs;
        const compName = this.fmtSvc.titleCase(descriptor.dimensionComponent?.plural ?? 'items');
        const compNameSglr = this.fmtSvc.titleCase(descriptor.dimensionComponent?.name ?? 'item');
        return [
            {
                type: 'dynamic',
                changeEmitter: evt,
                element: () => {
                    if (model.agg === 'any' || model.rank === 'none' || !model.rank) {
                        return [];
                    }
                    const subsetComparitorOptions: Array<{ value: OptionComponentSubset['subsetComparitor']; label: string }> = [
                        { value: 'pct-all', label: `Portion vs All ${compName}` },
                        { value: 'pct-others', label: `Portion vs Other ${compName}` },
                        { value: 'pct-any', label: `Portion vs Any ${compNameSglr}` },
                        { value: 'agg-others', label: `Difference from Other ${compName}` },
                        { value: 'agg-any', label: `Difference from Any ${compNameSglr}` },
                    ];
                    return [
                        b
                            .type('string')
                            .options({ label: 'Compare By', icon: <Percentage />, defaultValue: 'pct-others', options: subsetComparitorOptions })
                            .presentation({ width: 200, align: 'center' })
                            .map((o) => o.subsetComparitor)
                            .bind(model),
                    ];
                },
            },
        ];
    }

    private createGridSubsetInput(): IAlertSettingsOption<any>[] {
        const model = { filter: [] as QueryExpr[] };

        return [
            this.createOption('grid-alert', `When any table rows match alert conditions`, '', model)
                .form((b, model, evt) => [
                    createFilterSettingsInput(model, 'filter', this.attrs.schemaCtx, undefined, undefined, { comparitorNoun: 'condition' }),
                    this.createValidationWarning(evt, () => (!model.filter.length ? ['Alert disabled, because no conditions are configured.'] : [])),
                ])
                .toQuery((model) => {
                    const xb = this.createXb();
                    const query: Query = {
                        Select: xb.select({
                            ct: xb.count(),
                        }),
                        Where: groupExprs('and', model.filter),
                    };
                    return {
                        query,
                    };
                })
                .describe((model) => {
                    return !model.filter.length ? '' : 'Alert when any table row meets all of the conditions';
                })
                .describeEval((model, res) => {
                    const ctRaw = res.EvaluatedData?.[0]?.ct ?? 0;
                    const ct = typeof ctRaw === 'number' ? ctRaw : 0;

                    const rowsLbl = ct === 1 ? 'row' : 'rows';
                    const triggeredRun: RichTextItem = {
                        text: res.ConditionMet ? ' Yes ' : ' No ',
                        type: 'variable',
                        varKey: 'result',
                        info: res.ConditionMet
                            ? 'Given the currently visualized data, the alert would be sent'
                            : 'Given the currently visualized data, the alert would NOT be sent',
                    };

                    return {
                        lines: [
                            ['Alert condition met: ', triggeredRun],
                            `Found ${this.fmtSvc.formatInt0Dec(ct)} ${rowsLbl} that match the alert conditions`,
                        ],
                    };
                })
                .updateModel((model, changes) => {
                    if (changes.has('dataset-id') || !changes.has('type')) {
                        const searchableOps = new Set(['and', 'or']);
                        const hasMissingFields = (op: QueryOperation) => traverseExpr(op, (x) => 'Field' in x && !this.attrs.getByAlias(x.Field));
                        const cleanOperations = (exprs: Array<QueryExpr | IQueryExpr>) => {
                            const cleaned: Array<QueryExpr | IQueryExpr> = [];
                            for (const expr of exprs) {
                                if ('Operation' in expr) {
                                    if (searchableOps.has(expr.Operation?.toLocaleLowerCase()) && expr.Operands) {
                                        cleanOperations(expr.Operands);
                                        if (expr.Operands.length) {
                                            cleaned.push(expr);
                                        }
                                    } else if (!hasMissingFields(expr as QueryOperation)) {
                                        cleaned.push(expr);
                                    }
                                }
                            }
                            exprs.splice(0, Infinity, ...cleaned);
                        };
                        cleanOperations(model.filter);
                    }
                    return true;
                })
                .done(),
        ];
    }

    private createKpiAlertOptionsInput(): IAlertSettingsOption<any>[] {
        const defaultMetric = this.attrs.metrics[0];
        const { comparitorOptions, optionLookup: compLookup } = this.getComparitorOptions();

        interface KpiAlertConditionModel {
            left: string;
            comparitor: string;
            rightValue: number;
            rightField: string;
            rightType: 'field' | 'value';
        }
        const inputBuilder = this.inputBuilder<KpiAlertConditionModel>();
        const defaultModel = { conditions: [] as Array<KpiAlertConditionModel> };
        const metricOptions = this.attrs.metrics.map((m) => ({ value: m.alias, label: m.name }));
        const getMetric = (alias: string) => this.attrs.getByAlias(alias) ?? defaultMetric;
        interface KpiAlertCondition {
            value: KpiAlertConditionModel;
            index: number;
            onRemove: () => void;
            onChange: (value: KpiAlertConditionModel) => void;
        }
        function KpiAlertCondition({ index, value: item, onRemove, onChange }: KpiAlertCondition) {
            const multiMetric = metricOptions.length > 1;
            const leftAttr = getMetric(item.left);
            const isPercent = leftAttr.format === 'percent';
            const handleChange = useCallback(() => {
                onChange(item);
            }, [onChange, item]);
            const toggleType = useCallback(() => {
                item.rightType = item.rightType === 'field' ? 'value' : 'field';
                if (item.rightType === 'value') {
                    item.rightField = '';
                }
                handleChange();
            }, [item.rightType, item, handleChange]);
            const isValueMode = !multiMetric || item.rightType === 'value';
            const leftSpec = useMemo(
                () =>
                    inputBuilder
                        .type('string')
                        .options({ label: 'KPI', icon: <Hash />, options: metricOptions })
                        .presentation({ align: 'center', width: 200 })
                        .map((o) => o.left)
                        .bind(item),
                [item]
            );
            const comparitorSpec = useMemo(
                () =>
                    inputBuilder
                        .type('string')
                        .options({ label: 'Comparison Type', options: comparitorOptions, icon: <EqualNot /> })
                        .presentation({ align: 'center', width: 200 })
                        .map((o) => o.comparitor)
                        .bind(item),
                [item]
            );
            const rightValueSpec = useMemo(
                () =>
                    inputBuilder
                        .type('number')
                        .options({ label: 'Alert Threshold', icon: <Bell />, defaultValue: 0, mask: isPercent ? 'percent' : undefined })
                        .presentation({ width: multiMetric ? 166 : 200 })
                        .map((o) => o.rightValue)
                        .bind(item, handleChange),
                [item, multiMetric, handleChange]
            );
            const rightFieldSpec = useMemo(
                () =>
                    inputBuilder
                        .type('string')
                        .options({ label: 'Alert Threshold', icon: <Bell />, options: metricOptions })
                        .presentation({ align: 'center', width: 166 })
                        .map((o) => o.rightField)
                        .bind(item, handleChange),
                [item, item.left, handleChange]
            );

            return (
                <>
                    <SettingsInputRow>
                        <SettingsLabel>Alert Condition {index + 1}</SettingsLabel>
                        <Tooltip withinPortal label="Remove this condition" withArrow>
                            <ActionIcon onClick={onRemove}>
                                <Trash size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </SettingsInputRow>
                    <SettingsInput spec={leftSpec} onChanged={handleChange} />
                    <SettingsInput spec={comparitorSpec} onChanged={handleChange} />
                    <SettingsInputRow>
                        <SettingsLabel icon={<Bell />}>Alert Threshold</SettingsLabel>
                        <Group spacing={4} noWrap>
                            {isValueMode ? <InputSpecInput spec={rightValueSpec} /> : <InputSpecInput spec={rightFieldSpec} />}
                            {!multiMetric ? null : (
                                <ExprPickerAdornment
                                    color="primary"
                                    tooltip={isValueMode ? 'Switch to manually entry' : 'Switch to KPI picker'}
                                    icon={isValueMode ? List : Keyboard}
                                    onClick={toggleType}
                                />
                            )}
                        </Group>
                    </SettingsInputRow>
                </>
            );
        }

        function KpiAddCondition({ onAdd, isFirst }: { onAdd: () => void; isFirst: boolean }) {
            const which = isFirst ? 'an' : 'another';
            const text = `Add ${which} alert condition...`;
            return <AnchorButton icon={<Plus size={14} />} size="sm" text={text} onClick={onAdd} />;
        }
        const createOnAdd = (model: typeof defaultModel, evt: EventEmitter<void>) => () => {
            model.conditions.push({ left: defaultMetric.alias, comparitor: 'gt', rightValue: 0, rightType: 'value', rightField: '' });
            evt.emit();
        };
        const createOnRemove = (model: typeof defaultModel, index: number, evt: EventEmitter<void>) => () => {
            model.conditions.splice(index, 1);
            evt.emit();
        };
        const validateModel = (model: typeof defaultModel) => {
            const result: string[] = [];
            if (!model.conditions.length) {
                result.push('No alert conditions are configured');
            }
            const { hasDupe, hasEmpty, validItems } = model.conditions.reduce(
                ({ hasDupe, hasEmpty, validItems }, item) => {
                    const isDupe = item.rightType === 'field' && item.rightField === item.left && !!item.left;
                    const isEmpty =
                        (item.rightType === 'field' && !item.rightField) || (item.rightType === 'value' && typeof item.rightValue !== 'number');
                    const isValid = !isDupe && !isEmpty && !!item.left && !!item.comparitor;
                    if (isValid) {
                        validItems.push({ index: validItems.length, item });
                    }
                    return { hasDupe: hasDupe || isDupe, hasEmpty: hasEmpty || isEmpty, validItems };
                },
                { hasDupe: false, hasEmpty: false, validItems: [] as { index: number; item: KpiAlertConditionModel }[] }
            );
            if (hasEmpty) {
                result.push('Some conditions are not fully configured.');
            }
            if (hasDupe) {
                result.push('Some conditions are comparing the same KPI.');
            }
            return { issues: result, validItems };
        };
        return [
            this.createOption('kpi-alert', `When KPI reach a threshold`, '', defaultModel)
                .form((_, model, evt) => [
                    this.createDynamic(evt, () => [
                        ...model.conditions.flatMap((item, index) => {
                            const result: FormElement[] = [
                                {
                                    type: 'custom',
                                    component: KpiAlertCondition,
                                    value: item,
                                    onChange: () => evt.emit(),
                                    props: {
                                        index,
                                        onRemove: createOnRemove(model, index, evt),
                                    },
                                },
                                { type: 'divider' },
                            ];
                            return result;
                        }),
                        {
                            type: 'component',
                            component: KpiAddCondition,
                            props: {
                                isFirst: model.conditions.length === 0,
                                onAdd: createOnAdd(model, evt),
                            },
                        },
                        this.createDynamic(evt, () => {
                            const { validItems, issues } = validateModel(model);
                            const result: FormElement[] = [];
                            if (!validItems.length) {
                                result.push({ type: 'info', content: issues, status: 'error', label: 'Alert Disabled' });
                            } else if (issues.length) {
                                result.push({ type: 'info', content: issues, status: 'warning', label: 'Some conditions are invalide' });
                            }

                            return result;
                        }),
                    ]),
                ])
                .toQuery((model) => {
                    const { validItems } = validateModel(model);
                    let query: Query | undefined = undefined;
                    let condition: QueryExpr | undefined = { Value: false };
                    if (validItems.length) {
                        const xb = this.createXb();
                        const itemValues: QueryExpr[] = [];
                        condition = { Operation: 'or', Operands: itemValues };
                        query = { Select: [] };
                        for (const validItem of validItems) {
                            const { index, item } = validItem;
                            const { comparitor, left, rightType, rightField, rightValue } = item;
                            const rightExpr = rightType === 'field' ? xb.model[rightField] : xb.param(rightValue);
                            const expr = xb.op<boolean>(comparitor, xb.model[left], rightExpr);
                            const alias = `item${index}`;

                            query.Select!.push({ Alias: alias, Expr: xb.resolve(expr) });
                            itemValues.push(xb.resolve(xb.op<boolean>('eq', xb.model[alias], xb.param(true))));
                        }
                    }
                    return {
                        query,
                        condition,
                    };
                })
                .describe((model) => {
                    const { validItems } = validateModel(model);
                    const allValid = validItems.length === model.conditions.length;
                    return validItems.length === 0
                        ? ''
                        : validItems.length === 1
                        ? `Alert when the alert conditions are met`
                        : `Alert when any of the ${validItems.length} ${allValid ? '' : 'valid'} alert conditions are met`;
                })
                .describeEval((model, res) => {
                    const error = this.getEvaluationError(res);
                    if (error) {
                        return error;
                    }
                    const { validItems } = validateModel(model);

                    const data = res.EvaluatedData?.[0] ?? {};

                    const trueLbl = this.fmtSvc.titleCase(`${true}`);
                    const falseLbl = this.fmtSvc.titleCase(`${false}`);
                    const triggeredRun: RichTextItem = {
                        text: res.ConditionMet ? ' Yes ' : ' No ',
                        type: 'variable',
                        varKey: res.ConditionMet ? trueLbl : falseLbl,
                        info: res.ConditionMet
                            ? 'Given the currently visualized data, the alert would be sent'
                            : 'Given the currently visualized data, the alert would NOT be sent',
                    };

                    return {
                        lines: [
                            ['Any alert conditions met: ', triggeredRun],
                            {
                                items: validItems.map(({ index }): RichTextRuns => {
                                    const valueLbl = !!data[`item${index}`] ? trueLbl : falseLbl;
                                    const metLbl: RichTextRun = { text: valueLbl, type: 'variable', varKey: valueLbl };
                                    return [`Condition #${index + 1} result: `, metLbl];
                                }),
                                type: 'bullet',
                            },
                        ],
                    };
                })
                .updateModel((model, changes) => {
                    let updated = false;
                    if (!changes.has('type')) {
                        updated = true;
                        const conditions = model.conditions.splice(0, Infinity);
                        for (const item of conditions) {
                            if (this.attrs.getByAlias(item.left)) {
                                model.conditions.push(item);
                                if (!this.attrs.getByAlias(item.rightField)) {
                                    item.rightField = '';
                                }
                                if (item.rightType === 'field' && !item.rightField) {
                                    item.rightType = 'value';
                                    item.rightValue ??= 0;
                                }
                            }
                        }
                    }
                    return updated;
                })
                .done(),
        ];
    }

    private getComparitorOptions() {
        const comparitorOptions: Array<{ value: OptionComponentSubset['comparitor']; label: string }> = [
            { value: 'gt', label: 'Greater than' },
            { value: 'gte', label: 'Greater than or equal to' },
            { value: 'lt', label: 'Less than' },
            { value: 'lte', label: 'Less than or equal to' },
            { value: 'eq', label: 'Equal to' },
            { value: 'ne', label: 'Not equal to' },
        ];
        const optionLookup = comparitorOptions.reduce((result, item) => result.set(item.value, item.label), new Map<string, string>());

        return { comparitorOptions, optionLookup };
    }

    private createDimRankPicker(model: OptionComponentSubset, icon: ReactNode, includeFilter: boolean = true, defaultValue: 'asc' | 'desc' = 'asc') {
        const dimComp = this.attrs.descriptor.dimensionComponent;
        if (dimComp) {
            const { plural, name, asc, desc } = dimComp;
            type Item = { value: 'asc' | 'desc' | 'none' | 'filter'; label: string };
            const options = [
                { value: 'none', label: `All ${plural}` },
                { value: 'asc', label: `# ${this.fmtSvc.titleCase(plural)} ${asc}` },
                { value: 'desc', label: `# ${this.fmtSvc.titleCase(plural)} ${desc}` },
            ] as Item[];
            const dim = this.attrs.hasDimensions ? this.attrs.dimensions[0] : null;
            if (includeFilter && dim) {
                options.push({ value: 'filter', label: `Specific ${dim.name}(s)` });
            }

            return this.inputBuilder<typeof model>()
                .type('string')
                .options({ label: `${this.fmtSvc.titleCase(name)} Selection`, icon, defaultValue, options })
                .presentation({ width: 200, align: 'center' })
                .map((o) => o.rank)
                .bind(model);
        }
    }
    //#endregion

    //#region Standard options
    private createStandardOptions(): IAlertSettingsOption[] {
        return [...this.attrs.metrics.flatMap((t) => this.abnormalMetrics(this.idGen('std', t), t))];
    }

    private abnormalMetrics(id: (subId: string) => string, attr: IComponentAttribute): AlertSettingsOption[] {
        const xb = this.createXb();
        const attrs = this.attrs.subset((t) => t !== attr);
        const result: AlertSettingsOption[] = [];
        const metricInputMode = attrs.hasMetrics ? undefined : ('constant' as const);
        if (!attrs.hasDimensions) {
            result.push(
                this.createOption(id('abmet-max'), `When total ${attr.name} is above...`, '', { input: { Value: 0 } })
                    .form((b, model) => [
                        this.createNumberInput(b, 'Alert threshold', { attrs, mode: metricInputMode })
                            .map((o) => o.input)
                            .bind(model),
                    ])
                    .toQuery((model) => ({
                        query: { Select: xb.select({ total: xb.sum(xb.model[attr.alias]) }) },
                        condition: xb.resolve(xb.model.total.gt(xb.fromExpr(model.input))),
                    }))
                    .done(),
                this.createOption(id('abmet-min'), `When total ${attr.name} is below...`, '', { input: { Value: 0 } })
                    .form((b, model) => [
                        this.createNumberInput(b, 'Alert threshold', { attrs, mode: metricInputMode })
                            .map((o) => o.input)
                            .bind(model),
                    ])
                    .toQuery((model) => ({
                        query: { Select: xb.select({ total: xb.sum(xb.model[attr.alias]) }) },
                        condition: xb.resolve(xb.model.total.gt(xb.fromExpr(model.input))),
                    }))
                    .done(),

                this.createOption(id('abmet-range'), `When total ${attr.name} is above or below...`, '', { min: { Value: 0 }, max: { Value: 0 } })
                    .form((b, model) => [
                        this.createNumberInput(b, 'Alert above', { attrs, mode: metricInputMode })
                            .map((o) => o.max)
                            .bind(model),
                        this.createNumberInput(b, 'Alert below', { attrs, mode: metricInputMode })
                            .map((o) => o.min)
                            .bind(model),
                    ])
                    .toQuery((model) => ({
                        query: { Select: xb.select({ total: xb.sum(xb.model[attr.alias]) }) },
                        condition: xb.resolve(xb.or(xb.model.total.gt(xb.fromExpr(model.max)), xb.model.total.lt(xb.fromExpr(model.min)))),
                    }))
                    .done()
            );
        }

        return result;
    }
    //#endregion

    private idGen = (prefix: string, ...attrs: IComponentAttribute[]) => {
        const baseId = `${prefix}/[${attrs.map((t) => t.id).join(',')}]`;
        return (subId: string) => `${baseId}/${subId}`;
    };

    private createXb() {
        const { builder: xb } = exprBuilder<Record<string, number>>();
        return xb as ISupportSubQuery & typeof xb;
    }

    private createDynamic(changeEmitter: EventEmitter<void>, element: () => FormElement[]): FormElement {
        return {
            type: 'dynamic',
            changeEmitter,
            element,
        };
    }

    private createCondition(changeEmitter: EventEmitter<void>, condition: () => boolean, ...elements: FormElement[]): FormElement {
        return {
            elements,
            changeEmitter,
            condition,
            type: 'conditional',
        };
    }

    private createNumberInput<T>(
        b: IInputSpecBuilderType<T>,
        label: string,
        options: {
            attrs?: IComponentAttributeSet;
            defaultMode?: 'expr' | 'constant';
            fractions?: number;
            freeform?: boolean;
            icon?: ReactNode;
            mode?: 'expr' | 'constant';
            operations?: string[];
            percent?: boolean;
        }
    ) {
        let { fractions, freeform: acceptConstant = true, defaultMode, icon, operations, attrs = this.attrs, mode, percent } = options;
        acceptConstant = acceptConstant || mode === 'constant';
        defaultMode ??= mode !== 'constant' ? 'expr' : 'constant';
        const mask = percent ? ('percent' as const) : undefined;
        const acceptExpr = mode !== 'constant';

        return b
            .type('number-expr')
            .options({ label, schemaCtx: attrs.schemaCtx, acceptConstant, defaultMode, fractions, operations, acceptExpr, icon, mask })
            .presentation({ width: 120, align: 'right' });
    }

    private createValidationWarning(evt: EventEmitter<void>, getIssues: () => string[], messagePreface?: string) {
        return this.createDynamic(evt, () => {
            const issues = getIssues();
            const result: FormElement[] = [];
            if (issues.length) {
                if (messagePreface) {
                    issues.unshift(messagePreface);
                }
                result.push(
                    { type: 'divider' },
                    {
                        type: 'info',
                        label: 'Alert Disabled',
                        status: 'warning',
                        content: issues,
                    }
                );
            }
            return result;
        });
    }

    private getEvaluationError(evalResult: UdEvaluatedAlertCondition) {
        const { EvaluationFailed, EvaluationIssue } = evalResult;
        if (EvaluationFailed) {
            return EvaluationIssue;
        }
    }

    private createDimPickerInput<T>(
        model: T,
        field: keyof T,
        label: string,
        options: Partial<IMultiItemPickerSpec<string>> = {},
        presentation: IMultiItemPickerSpec<string>['presentationOptions'] = {}
    ) {
        const [dim] = this.attrs.dimensions;
        const { datasource, schemaSvc } = this.attrs.schemaCtx;
        const valueProvider = !dim ? null : datasource.getValueProviderFactory?.(schemaSvc).getValueProvider({ Field: dim.alias });
        const getValues = async () => {
            const values: string[] = typeof valueProvider === 'function' ? await valueProvider?.('', 10000) : [];
            return values;
        };
        const nameAccessor = (item: string) => item;
        const b = this.inputBuilder<T>();

        return b
            .type('item-picker')
            .options({ label, items: getValues, mode: 'multiple', nameAccessor, ...options })
            .presentation({ width: 200, placeholder: `Select ${dim.name}(s)`, ...presentation })
            .map((o) => (o as any)[field] as string[])
            .bind(model);
    }

    private createExprInput<T>(
        b: IInputSpecBuilderType<T>,
        type: 'number-expr' | 'string-expr' | 'date-expr',
        label: string,
        freeform: boolean,
        defaultMode?: 'expr' | 'constant',
        operations?: string[]
    ) {
        return b.type<typeof type>(type).options({ label, schemaCtx: this.attrs.schemaCtx, acceptConstant: freeform, defaultMode, operations });
    }

    private createOption<T>(id: string, name: string, description: string, defaultModel: T) {
        type CreateFormCallback = (inputBuilder: IInputSpecBuilderType<T>, model: T, changeEmitter: EventEmitter<void>) => FormElement[];
        type CreateQueryCallback = NonOptional<IAlertSettingsOption<T>['toQuery']>;
        type CreateDescribeCallback = NonOptional<IAlertSettingsOption<T>['describe']>;
        type CreateDescribeEvalCallback = NonOptional<IAlertSettingsOption<T>['describeEval']>;
        type CreateUpdateModelCallback = NonOptional<IAlertSettingsOption<T>['updateModel']>;
        const changeEmitter = EventEmitter.empty();
        let option: IAlertSettingsOption<T> = {
            id,
            name,
            defaultModel,
            onChange: changeEmitter.emit,
            form: () => [],
            toQuery: () => ({}),
            describe: () => '',
            describeEval: (): RichTextItem => '',
            updateModel: () => false,
        };

        return {
            form: (callback: CreateFormCallback) => {
                const inputBuilder = this.inputBuilder<T>();
                option.form = (model: T) => callback(inputBuilder, model, changeEmitter);
                return {
                    toQuery: (callback: CreateQueryCallback) => {
                        option.toQuery = (model: T) => callback(model);
                        let optionBuilder = {
                            done: () => ({ ...option }),
                            describeEval: (callback: CreateDescribeEvalCallback) => {
                                option = {
                                    ...option,
                                    describeEval: (model: T, evalResult: UdEvaluatedAlertCondition) => callback(model, evalResult),
                                };
                                return optionBuilder;
                            },
                            describe: (callback: CreateDescribeCallback) => {
                                option = { ...option, describe: (model: T) => callback(model) };
                                return optionBuilder;
                            },
                            updateModel: (callback: CreateUpdateModelCallback) => {
                                option = { ...option, updateModel: (currentModel, changes) => callback(currentModel, changes) };
                                return optionBuilder;
                            },
                        };
                        return optionBuilder;
                    },
                };
            },
        };
    }
    private inputBuilder<T>() {
        return InputSpecBuilder.create<T>();
    }
}

export function describeAlert(alert: UserDefinedNotificationThreshold) {
    const fmtSvc = FormatService.instance;
    const settings = (alert.PresentationOptions as IThresholdPresentationOptions)?.settings;
    const firstSetting = settings?.[0]?.brief;
    const otherSettings = settings?.length && settings.length > 1 ? `... (${settings.length - 1} others)` : '';
    const brief = !firstSetting ? 'No alert conditions' : firstSetting + otherSettings;
    const description = !settings?.length
        ? ''
        : fmtSvc.userFriendlyJoin(
              settings.map((s) => s.description),
              'or'
          );
    return {
        icon: BellRinging,
        brief,
        description,
        type: 'Alert',
    };
}
